import { and, assign, not, raise, setup, stateIn } from 'xstate';
import { AVATAR_PEER_RETRY_DELAY, ICE_SERVER_WAIT_TIMEOUT } from '../constants';
import { calculateReconnectDelay } from '../utils';
import * as actions from './actions';
import * as guards from './guards';
import type { VoiceLiveContext, VoiceLiveEvent, VoiceLiveInput } from './types';

/**
 * Voice Live session statechart.
 *
 * A parallel chart with three regions:
 *   - `wsSession` — WebSocket lifecycle (disconnected → connecting → connected →
 *     ready) with exponential-backoff reconnect.
 *   - `avatar` — avatar gating: tracks whether the WebRTC peer comes up
 *     (avatarPending → retrying → avatarActive) or the session runs without one
 *     (`audioOnly`, reached voluntarily via `defaultVideoEnabled: false` or by
 *     degrading). "Was it a degrade?" is carried by `context.degradedToAudio`, not
 *     by the state name, so the two audio-only routes share one state.
 *     A raised RECONNECT_SESSION event rebuilds the WS session with the correct mode.
 *     Mode switches are symmetric: REATTEMPT (audio → video) and SWITCH_TO_AUDIO
 *     (video → audio, unless the live peer can just drop its video track).
 *   - `network` — browser connectivity (online/offline). The reconnect backoff is
 *     gated on this region so an offline browser parks without burning the retry budget.
 *
 * **In the machine (pure, unit-tested):** all state transitions, guards, delays,
 * and context-mutating `assign` actions.
 *
 * **Outside the machine (imperative shell):** the effect actions (see
 * `actions.ts`) are no-op stubs; `useVoiceLiveSession` replaces them with real
 * implementations via `.provide()` at the React boundary.
 */

export const voiceLiveMachine = setup({
  types: {
    context: {} as VoiceLiveContext,
    events: {} as VoiceLiveEvent,
    input: {} as VoiceLiveInput,
  },
  actions: {
    // Context-mutating assigners (logic lives in ./actions; assign() here is
    // where XState binds the full machine types, notably the event union).
    resetForStart: assign(actions.resetForStart),
    prepareManualRetry: assign(actions.prepareManualRetry),
    prepareConnectingAttempt: assign(actions.prepareConnectingAttempt),
    onWsOpen: assign(actions.onWsOpen),
    onSessionReady: assign(actions.onSessionReady),
    onFinalDisconnect: assign(actions.onFinalDisconnect),
    onMicCaptureFailed: assign(actions.onMicCaptureFailed),
    incrementRetry: assign(actions.incrementRetry),
    storeIceServers: assign(actions.storeIceServers),
    storeFieldOneSession: assign(actions.storeFieldOneSession),
    setError: assign(actions.setError),
    setVideoEnabled: assign(actions.setVideoEnabled),
    resetConnectionOnStop: assign(actions.resetConnectionOnStop),
    markAvatarActive: assign(actions.markAvatarActive),
    countAvatarFailure: assign(actions.countAvatarFailure),
    setAvatarDropError: assign(actions.setAvatarDropError),
    enterAudioFallback: assign(actions.enterAudioFallback),
    reattemptAvatar: assign(actions.reattemptAvatar),
    enterVoluntaryAudio: assign(actions.enterVoluntaryAudio),
    resetFallback: assign(actions.resetFallback),
    // Effect stubs — real implementations provided by useVoiceLiveSession via .provide().
    createWebSocket: actions.createWebSocket,
    teardown: actions.teardown,
    forceCloseSocket: actions.forceCloseSocket,
    handleIceServerTimeout: actions.handleIceServerTimeout,
    maybeAutoStartAvatar: actions.maybeAutoStartAvatar,
    retryAvatarPeer: actions.retryAvatarPeer,
    detachAvatarVideo: actions.detachAvatarVideo,
  },
  guards: { ...guards },
  delays: {
    /** Exponential backoff (capped) keyed on the current retry count. */
    reconnectBackoff: ({ context }) => calculateReconnectDelay(context.retryCount),
    iceServerWait: ICE_SERVER_WAIT_TIMEOUT,
    avatarPeerRetry: AVATAR_PEER_RETRY_DELAY,
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QDcD2BLAxmAMu5YAdJqgHaliYAu6ZAxAMoAqA8gAoDaADALqKgAHVLHQ0y-EAA9EARhkB2ADQgAnogCsANgDMhABwAWLps16ZBiwCYu2gL63laLLnxES5SmNJ0A6gwD6AKIASsEswdx8SCBCIl4S0ghyXJaElgaWljI62upGBurKaghmhAYAnJXlFera2uby8vaOGNh4BMRkFNS03gCSAMKB-gwhAGohDJESsaK9CbJc5anqeurlmnkZ5cZFsquahFzyXAaa8np65SeaBs0gTm2unR499ABifYE4ACIsAHLDUYMBh9AH+AbBQIAQSYgR+02is3i0USMhs6kI2gu5Xqejq8nUMkKqlkBiuZWW2ksROqjUJ90eLg67m6XkIEHQsFZnkgjCY0OCTERgmEc3EqNk2Xk+ms2gycmpMksewQ2nKMix5yy8gKGVM5UZrWZbi6nl6HK5POofIAstD-gBVaE4fxQpjBACaIpiYpRoDR0tlNgVMiVKtJCCJmJS8r0t0sZnk6SNznapte7OtNFIUF8AXYgX+PuR80lSR2XCOa0qKU0lnkxO0qrDmirGoMMj06Uqhj0TQcD2N6ZebIt2fQufzEJwLFGJb9ZYDsl1moUMmWYZx6i0LZsMoUtUsmi7V1uelTTxZZrepFH5qnfhnc8CHBkUVFcSXUhX3cIq3WXU6mPY4lEjdIZXUC5zBPYw9FOAwBxaNNngnMh7x6KcWHed4cD6QEFy-CVlwQawNn-eptC4LgFBObRNFVRtMSg7Eu02Gt40vE0MKzG8czzSRYCoABDKgiGEgAzMSACcAApnAYMBpIIaSfGE0QAEo6CZEc0LvCdc0I8VSAWUiuFWf88nkbFyXjWowOKetNUqHdGkTWtLC43S+PHPi+WBUFwUdNgflheEjP9H9SL-U5TEJMwzHWU49x0QhG3RIxzOlOovNQnz0OzPknwGWd514GZF2IqLE1SWL+1WLtiR2AxVXg5jjiuGssq7XLr0zXzMyKgISpfN8P19IiTPLGqjjOeqEqa5LI3rGV62udUKlPRDeozMcCr8iA6Gw3D8NfcqkUqqaSJmur4sapKWsjZUFEIZUiTkYlLG0Qwdp4i1pLAYSIBUacRrK8bSyqxIDVe9d41MYx1HMvdMn0ejTmODVcW+369MIAGgZB4rStfd8Ksm0yYbkRt4fgzZkcjZZDhpHF4OuajtsHHS8v69CCeBo6cLwgjzs-YzKYpam2IR+mSWKdFrKxdH1Xgzbrlx-K7wBgy8wBYWzohy7TIUTtXsbTdsSuHcGMjeRNjSwwTGpS5OygjXea1ygfMfYaSYi780QMOoHY1czj2WKC9FVdb9Go6ldUyayUy54ceb2z2ddBv2yYuinyzkc5g3lLIwyyCNHO+whzmuHcjGseL3fT-Gvf6qdBJEsTCEkmTZO1viACFhMwABrVAJIkrTub6pu+9bqB-ahldTfXC3t13Rnsn-DdzLWTtLFxC8U5QjoJOEgAbM+ACMh+H-lBWFUWJvF8trlVMiDy7a4g+ML67iPq8iCnwvtfEe-J2ALyulFV+4FqJVjyFwfE2RFSnEPshABhAgFXxvnQe0ToXRukCB6b0j9IaQMSNA+WOxdAbGPJcDUOg7a-UwSA2+0IxiwkFP4aEAwmB9AmBA42CCDBo3KP2DYQd5QM3lpoHYVdySwVWGcXInl-7cWYTfLuyBRLCWkmwMApBORTjYRw4I-h3jQj6DgcKJCjbljOMI0u+JvpWHSDSN+SxhFLHVLqPUiZNBMPPlgkemjtG6P0YYvMxiBSmPMZY8KOcxaRUSPY16zs6iGAyK4uWiBMhGCxPiU4qwaKEnMgE4BGiAZUGkioSckT2HRLMRYqxCIbF5xIhcYReQZEpBZgUQk7iuyWQqFRWC3ZSmqJHOo4JlTqm1LoFEzhsTmljXJs-EiyRhEbCWDcG4Owo4wPxEcPIGowwNFEf4iZzwpnD2blUmpbchKiXElJJSslhJaJEmEpSwQwB3MnqnE+gSWG3NmYZVpayoptlSLiQkNlQIbGyWZaiW9Vh1AQTuK4dhLmAvKcE4SABXTkqAWCkDPiDKEsI4S2jYA-Q2bSorKmPFiN6iEThrFyC2NYhxkznBPNZMwPZ7CDlIKgCAcAJBTzAKspJshVQAFpDiwJoksaoojuzVEbuaKqpDKblBbG2I40EjDEjWDRP+aDuJ405NyA60qA6IEqJqJGsCGxUPJI9RyRJXpxUMNSL6CZNW3j+rmO1i8zKVDStZJGBRTXlERUmV6RJ4xhlqOoBsFyLXeQ9jxSAoayFSgcrIbEug-GrHgjYE1KjM1py1Z7QmebTKKLKPvGRG4Gxcr1U9BBMoaQ0TkFcY8FhUFDmPrtWtzcdYNvzkjPQqTzjwILhuG28sUjlCxHHQduQabqDKUE4eU6SIVBbHbNdmRtSIXDloXdwL3mhL0QY2pB7qoan0NGO25kEFUXLjk9Ea6LhUSUbiONhpsWAKBRU35oKoBPsSHQrEiZiQ0VNYYN+yoqxhiotZKiUFhnXo0QSolJKyUwbJHINIVw5Dkk3CkN+x4ZT4hPGYFIZ4aRCtsEAA */
  id: 'voiceLive',
  type: 'parallel',
  context: ({ input }) => ({
    userId: input.userId,
    scenarioId: input.scenarioId,
    savedUserId: '',
    fieldOneSessionId: null,
    iceServers: [],
    hasIceServers: false,
    isVideoEnabled: input.defaultVideoEnabled,
    socketIsAudioOnly: !input.defaultVideoEnabled,
    degradedToAudio: false,
    avatarEverActive: false,
    initialAvatarFailures: 0,
    retryCount: 0,
    shouldReconnect: true,
    sessionReady: false,
    errorMessage: '',
  }),
  on: {
    SET_ERROR: {
      actions: { type: 'setError', params: ({ event }) => ({ message: event.message }) },
    },
    SET_VIDEO_ENABLED: {
      actions: { type: 'setVideoEnabled', params: ({ event }) => ({ enabled: event.enabled }) },
    },
  },
  states: {
    // ── WebSocket session lifecycle ───────────────────────────────────────
    wsSession: {
      initial: 'disconnected',
      // Handled regardless of sub-state:
      on: {
        STOP: { target: '.disconnected', actions: ['resetConnectionOnStop', 'teardown'] },
        MIC_CAPTURE_FAILED: {
          target: '.disconnected',
          actions: [
            { type: 'onMicCaptureFailed', params: ({ event }) => ({ message: event.message }) },
            'teardown',
          ],
        },
        ICE_SERVERS: {
          actions: [
            { type: 'storeIceServers', params: ({ event }) => ({ servers: event.servers }) },
            'maybeAutoStartAvatar',
          ],
        },
        FIELDONE_SESSION_CREATED: {
          actions: {
            type: 'storeFieldOneSession',
            params: ({ event }) => ({ sessionId: event.sessionId }),
          },
        },
        // One WS-close handler for the whole region: reconnect while allowed,
        // otherwise settle in `disconnected` with a user-facing error. The
        // `reconnecting` sub-state overrides this with a re-entering variant so
        // a socket that dies mid-backoff restarts the wait.
        WS_CLOSE: [
          {
            guard: { type: 'shouldReconnect', params: ({ event }) => ({ code: event.code }) },
            target: '.reconnecting',
          },
          {
            target: '.disconnected',
            actions: { type: 'onFinalDisconnect', params: ({ event }) => ({ code: event.code }) },
          },
        ],
        // Raised by the avatar region (enter audio fallback / re-attempt video):
        // rebuild the WS session. Audio-vs-video mode is derived from context
        // (isVideoEnabled) when the socket is built.
        RECONNECT_SESSION: { target: '.connecting', actions: 'createWebSocket' },
      },
      states: {
        disconnected: {
          on: {
            START: { target: 'connecting', actions: ['resetForStart', 'createWebSocket'] },
            MANUAL_RETRY: {
              target: 'connecting',
              actions: ['prepareManualRetry', 'createWebSocket'],
            },
          },
        },
        connecting: {
          entry: 'prepareConnectingAttempt',
          on: {
            WS_OPEN: { target: 'connected', actions: 'onWsOpen' },
          },
          after: {
            iceServerWait: { actions: 'handleIceServerTimeout' },
          },
        },
        // `connected` = socket open + authed; `ready` = backend `session.updated`
        // received (audio may flow). Kept distinct as separate UI/connection states.
        connected: {
          on: {
            SESSION_UPDATED: {
              target: 'ready',
              actions: ['onSessionReady', 'maybeAutoStartAvatar'],
            },
            OFFLINE: { target: 'reconnecting', actions: 'forceCloseSocket' },
          },
        },
        ready: {
          // Tagged so the UI can ask "is the session live?" without matching state names.
          tags: ['sessionActive'],
          on: {
            OFFLINE: { target: 'reconnecting', actions: 'forceCloseSocket' },
          },
        },
        reconnecting: {
          on: {
            ONLINE: {
              target: 'connecting',
              actions: ['incrementRetry', 'createWebSocket'],
            },
            // Re-entering variant (overrides the region-level WS_CLOSE): a socket
            // that dies mid-backoff restarts the wait rather than settling.
            WS_CLOSE: [
              {
                guard: { type: 'shouldReconnect', params: ({ event }) => ({ code: event.code }) },
                target: 'reconnecting',
                reenter: true,
              },
              {
                target: 'disconnected',
                actions: {
                  type: 'onFinalDisconnect',
                  params: ({ event }) => ({ code: event.code }),
                },
              },
            ],
          },
          after: {
            // Only auto-retry while the browser reports connectivity (tracked by
            // the `network` region). When offline we park here (no retries
            // burned) until ONLINE moves `network` back to `online`.
            reconnectBackoff: {
              guard: stateIn({ network: 'online' }),
              target: 'connecting',
              actions: ['incrementRetry', 'createWebSocket'],
            },
          },
        },
      },
    },
    // ── Audio-only fallback gating ────────────────────────────────────────
    avatar: {
      initial: 'avatarPending',
      // Reset gating whenever the session (re)starts or stops:
      on: {
        START: { target: '.avatarPending', actions: 'resetFallback' },
        STOP: { target: '.avatarPending', actions: 'resetFallback' },
        MIC_CAPTURE_FAILED: { target: '.avatarPending', actions: 'resetFallback' },
        MANUAL_RETRY: { target: '.avatarPending', actions: 'resetFallback' },
        AVATAR_ACTIVE: { target: '.avatarActive', actions: 'markAvatarActive' },
        // Mode switches rebuild the socket, so both are gated on a running session:
        // `RECONNECT_SESSION` is region-wide in `wsSession` and would otherwise connect
        // straight out of `disconnected`, before the user hits Start Conversation.
        // Pre-start they record the preference only — the socket mode is derived from
        // it on the next `connecting` entry. Offline is gated the same way: the session
        // parks in `reconnecting` (not `disconnected`), so without the `network` check a
        // switch would un-park it and open a socket with no connectivity.
        REATTEMPT: [
          {
            guard: and([
              'isAudioOnlySession',
              not(stateIn({ wsSession: 'disconnected' })),
              stateIn({ network: 'online' }),
            ]),
            target: '.avatarPending',
            actions: ['reattemptAvatar', raise({ type: 'RECONNECT_SESSION' })],
          },
          {
            guard: and([
              'isAudioOnlySession',
              not(stateIn({ wsSession: 'disconnected' })),
              stateIn({ network: 'offline' }),
            ]),
            target: '.avatarPending',
            actions: 'reattemptAvatar',
          },
          {
            guard: 'isAudioOnlySession',
            actions: { type: 'setVideoEnabled', params: { enabled: true } },
          },
        ],
        // No peer is carrying audio yet, so a video-mode socket would leave the session
        // mute. `avatarActive` overrides this with an in-place video detach.
        SWITCH_TO_AUDIO: [
          {
            guard: and([
              not('isAudioOnlySession'),
              not(stateIn({ wsSession: 'disconnected' })),
              stateIn({ network: 'online' }),
            ]),
            target: '.audioOnly',
            actions: ['enterVoluntaryAudio', raise({ type: 'RECONNECT_SESSION' })],
          },
          {
            guard: and([
              not('isAudioOnlySession'),
              not(stateIn({ wsSession: 'disconnected' })),
              stateIn({ network: 'offline' }),
            ]),
            target: '.audioOnly',
            actions: 'enterVoluntaryAudio',
          },
          {
            guard: not('isAudioOnlySession'),
            actions: { type: 'setVideoEnabled', params: { enabled: false } },
          },
        ],
      },
      states: {
        avatarPending: {
          // XState has no dynamic `initial`, so an audio-only session lands here first
          // and falls straight through rather than implying a peer attempt.
          always: { guard: 'isAudioOnlySession', target: 'audioOnly' },
          on: {
            AVATAR_FAILED: [
              { guard: 'canRetryAvatar', target: 'retrying', actions: 'countAvatarFailure' },
              {
                guard: 'shouldEnterFallback',
                target: 'audioOnly',
                actions: [
                  'countAvatarFailure',
                  'enterAudioFallback',
                  raise({ type: 'RECONNECT_SESSION' }),
                ],
              },
            ],
          },
        },
        retrying: {
          // AVATAR_FAILED is intentionally NOT handled here: a single attempt's
          // failure already moved us out of avatarPending, so further failure
          // signals from the same attempt are ignored (structural dedupe).
          after: {
            avatarPeerRetry: { target: 'avatarPending', actions: 'retryAvatarPeer' },
          },
        },
        // Running without a peer, whether by choice or by degrading. Check
        // `context.degradedToAudio` to tell the two apart.
        audioOnly: {},
        // The avatar is up and healthy, so this state — not a shadow ref — is what
        // makes a drop here "post-success". Recovery belongs to the wsSession region:
        // force-closing the socket drives WS_CLOSE → reconnect (re-negotiating the
        // avatar) instead of waiting out the ~52s TCP timeout on a poor network.
        // With no reconnect pending there is nothing to recover, so surface the error.
        avatarActive: {
          on: {
            // The peer already carries the audio, so drop the video element only —
            // no rebuild, no audio gap.
            SWITCH_TO_AUDIO: {
              actions: [
                { type: 'setVideoEnabled', params: { enabled: false } },
                'detachAvatarVideo',
              ],
            },
            AVATAR_FAILED: [
              { guard: 'canReconnectSession', actions: 'forceCloseSocket' },
              { actions: 'setAvatarDropError' },
            ],
          },
        },
      },
    },
    // ── Browser connectivity ────────────────────────────────────
    // A tiny independent region mirroring the browser's online/offline status.
    // Kept separate so the connection region can gate its auto-reconnect backoff
    // (see `reconnecting.after`) on connectivity, and the UI can show an offline
    // indicator — without either coupling to the WebSocket lifecycle.
    network: {
      initial: 'online',
      states: {
        online: { on: { OFFLINE: 'offline' } },
        offline: { on: { ONLINE: 'online' } },
      },
    },
  },
});
